import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Bookmark, Check, CheckSquare, Download, FileUp, FolderPlus, FolderUp, Pencil, Send, Trash2, X } from "lucide-react";
import SendToEstimateDialog from "./SendToEstimateDialog";
import { sheetLabel } from "./sheetLabel";
import { useTakeoff } from "./store";
import { getPageState, pageKey } from "./pageState";
import { ACCEPT_EXT, CARD, INPUT_SM, MONO, fmtSize, isPdf, isTiff, fileExt } from "./theme";
import { naturalByName } from "./sheets";
import { ConfirmDialog } from "./dialogs";
import { openPdfDoc } from "./pdfSetup";
import { getUploadOptimize, setUploadOptimize } from "./api";
import { downloadMarkedSheets } from "./markedExport";
import UploadingRows from "./UploadingRows";
import { PlanFileRow, PlanFolderNode, MovePicker, TakeoffDot } from "./PlansTree";

/** Internal drag payload for moving files/folders within the job. */
const DND_TYPE = "application/x-takeoff-move";

/** Recursively collect files from a dropped item list (folders included). */
async function collectDropped(dataTransfer) {
  const out = [];
  const walk = (entry) => new Promise((res) => {
    if (entry.isFile) entry.file((f) => { out.push(f); res(); }, () => res());
    else if (entry.isDirectory) {
      const reader = entry.createReader();
      reader.readEntries(async (entries) => { for (const e of entries) await walk(e); res(); }, () => res());
    } else res();
  });
  const items = [...(dataTransfer.items || [])];
  const entries = items.map((i) => i.webkitGetAsEntry?.()).filter(Boolean);
  if (entries.length) { for (const e of entries) await walk(e); }
  else out.push(...(dataTransfer.files || []));
  return out;
}

export default function PlansPanel({ drawing, openDrawing, pageNum }) {
  const {
    doc, drawings, folders, uploadFiles, patchDrawing, removeDrawing,
    createFolder, renameFolder, deleteFolder, moveFolder, moveDrawings, mutate, viewer,
    activeId, uploads, activeProject,
  } = useTakeoff();
  const filesInput = useRef(null);
  const folderInput = useRef(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [newFolder, setNewFolder] = useState(null); // {parentId, category}
  const [folderName, setFolderName] = useState("");
  const [bmRename, setBmRename] = useState(null); // { key, value } — bookmark being renamed
  const [renaming, setRenaming] = useState(null); // {kind: "file"|"folder", id, name}
  const [upOpt, setUpOpt] = useState(() => getUploadOptimize()); // upload-time optimize ticks (persisted)
  const [delFolder, setDelFolder] = useState(null);
  const [delFile, setDelFile] = useState(null);
  const [moveId, setMoveId] = useState(null);
  const [pageSel, setPageSel] = useState(() => new Set());
  const [numPages, setNumPages] = useState(0);
  const [markBusy, setMarkBusy] = useState("");
  const [dragOver, setDragOver] = useState(false);
  // multi-select + internal move drag
  const [multiMode, setMultiMode] = useState(false);
  const [sel, setSel] = useState(() => new Set()); // "d:<drawingId>" | "f:<folderId>"
  const [movePicker, setMovePicker] = useState(false);
  const [dropTarget, setDropTarget] = useState(null); // folder_id | "root-plans" | "root-docs"

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = !needle ? drawings : drawings.filter((d) => d.name.toLowerCase().includes(needle));
    // natural filename order — the SAME order the viewer's folder arrows walk
    return [...list].sort(naturalByName);
  }, [drawings, q]);

  const drawingsById = useMemo(() => Object.fromEntries(drawings.map((d) => [d.drawing_id, d])), [drawings]);
  const foldersById = useMemo(() => Object.fromEntries(folders.map((f) => [f.folder_id, f])), [folders]);

  // A file that sits in a folder is listed with that folder no matter what its own category says —
  // the folder decides the tab (loose files at the root still split by category).
  const filesIn = (folderId, category) =>
    visible.filter((d) => (d.folder_id || null) === (folderId || null) && (folderId || (d.category || "plans") === category));
  const childFolders = (parentId, category) =>
    folders.filter((f) => (f.parent_id || null) === (parentId || null) && (f.category || "plans") === category).sort(naturalByName);

  /** Every folder nested under fid (any depth). */
  const descendantsOf = (fid) => {
    const out = new Set();
    const stack = [fid];
    while (stack.length) {
      for (const c of folders.filter((f) => (f.parent_id || null) === stack.pop())) {
        if (!out.has(c.folder_id)) { out.add(c.folder_id); stack.push(c.folder_id); }
      }
    }
    return out;
  };

  /** Recursive file count (files directly inside + all subfolders). */
  const recursiveFileCount = (fid) => {
    let n = 0;
    const stack = [fid];
    while (stack.length) {
      const cur = stack.pop();
      n += drawings.filter((d) => (d.folder_id || null) === cur).length;
      folders.filter((f) => (f.parent_id || null) === cur).forEach((f) => stack.push(f.folder_id));
    }
    return n;
  };

  const fileHasTakeoffs = (drawingId) => (doc?.items || []).some((i) => i.fileId === drawingId);

  // active drawing page count (for the mini-list)
  useEffect(() => {
    let alive = true;
    setNumPages(0);
    setPageSel(new Set());
    if (drawing && isPdf(drawing)) {
      (async () => {
        try {
          const docProxy = await openPdfDoc(drawing.drawing_id);
          if (alive) setNumPages(docProxy.numPages);
        } catch { /* leave 0 */ }
      })();
    } else if (drawing && isTiff(drawing)) {
      setNumPages(drawing.pages || 0); // TIFF pages counted server-side at conversion/prepare
    }
    return () => { alive = false; };
  }, [drawing?.drawing_id]); // eslint-disable-line

  const bookmarks = useMemo(() => {
    if (!doc) return [];
    return Object.entries(doc.pages || {})
      .filter(([, pg]) => pg.bookmarked)
      .map(([key, pg]) => {
        const [fid, pgNum] = key.split(":");
        const d = drawings.find((x) => x.drawing_id === fid);
        return d ? { drawing: d, page: Number(pgNum), name: pg.name || `Page ${pgNum}` } : null;
      })
      .filter(Boolean);
  }, [doc, drawings]);

  const toggleCollapsed = (id) => setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(folders.map((f) => f.folder_id)));

  const doUpload = async (files, folderId = null, category = null) => {
    const ok = [...files].filter((f) => ACCEPT_EXT.includes(`.${fileExt(f.name)}`));
    const skipped = files.length - ok.length;
    if (skipped > 0) toast.message(`${skipped} unsupported file${skipped > 1 ? "s" : ""} skipped`);
    if (ok.length) await uploadFiles(ok, { folderId, category });
  };

  // ---------------- multi-select + move ----------------
  const toggleSel = (key) => setSel((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const selPayload = () => ({
    drawingIds: [...sel].filter((k) => k.startsWith("d:")).map((k) => k.slice(2)),
    folderIds: [...sel].filter((k) => k.startsWith("f:")).map((k) => k.slice(2)),
  });
  const selectAll = () => {
    const n = new Set();
    visible.forEach((d) => n.add(`d:${d.drawing_id}`));
    folders.forEach((f) => n.add(`f:${f.folder_id}`));
    setSel(n);
  };
  const exitMulti = () => { setMultiMode(false); setSel(new Set()); setMovePicker(false); };

  const fileRidesWithFolder = (d, folderIds) => {
    let cur = d.folder_id || null;
    while (cur) {
      if (folderIds.includes(cur)) return true;
      cur = foldersById[cur]?.parent_id || null;
    }
    return false;
  };

  /** Move a mixed payload into a destination. Folders nested inside other
   * moved folders (and files inside moved folders) ride along — each item
   * moves exactly once. Scales/takeoffs/names hang off the records, so
   * nothing else changes. */
  const execMove = async ({ drawingIds, folderIds }, dest) => {
    const topFolders = folderIds.filter((fid) => !folderIds.some((o) => o !== fid && descendantsOf(o).has(fid)));
    const topDrawings = drawingIds.filter((id) => {
      const d = drawingsById[id];
      return d && !fileRidesWithFolder(d, topFolders);
    });
    let moved = 0, failed = 0;
    for (const fid of topFolders) {
      try { await moveFolder(fid, dest.folderId); moved++; }
      catch (e) { failed++; toast.error(e?.response?.data?.detail || "Couldn't move that folder"); }
    }
    if (topDrawings.length) {
      try {
        const r = await moveDrawings(topDrawings, dest.folderId, dest.category);
        moved += r.moved ?? topDrawings.length;
      } catch (e) { failed += topDrawings.length; toast.error(e?.response?.data?.detail || "Couldn't move those files"); }
    }
    if (moved) toast.success(`Moved ${moved} item${moved > 1 ? "s" : ""}${failed ? ` — ${failed} skipped` : ""}`);
    exitMulti();
  };

  const canDropFolderOn = (folderIds, targetFolderId) =>
    !folderIds.some((fid) => fid === targetFolderId || descendantsOf(fid).has(targetFolderId));

  // ---------------- drag & drop ----------------
  const startDrag = (e, key) => {
    const payload = multiMode && sel.has(key) ? selPayload()
      : key.startsWith("d:") ? { drawingIds: [key.slice(2)], folderIds: [] }
      : { drawingIds: [], folderIds: [key.slice(2)] };
    e.dataTransfer.setData(DND_TYPE, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "move";
  };
  const readPayload = (e) => {
    try { return JSON.parse(e.dataTransfer.getData(DND_TYPE)); } catch { return null; }
  };
  const isOsFileDrop = (e) => [...(e.dataTransfer?.types || [])].includes("Files");

  const onDropTo = async (e, dest) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTarget(null);
    setDragOver(false);
    if (isOsFileDrop(e)) {
      const files = await collectDropped(e.dataTransfer);
      if (files.length) doUpload(files, dest.folderId, dest.category);
      return;
    }
    const payload = readPayload(e);
    if (!payload || (!payload.drawingIds.length && !payload.folderIds.length)) return;
    if (dest.folderId && !canDropFolderOn(payload.folderIds, dest.folderId)) {
      toast.error("A folder can't be moved into itself or one of its subfolders");
      return;
    }
    await execMove(payload, dest);
  };
  const dragOverProps = (targetKey) => ({
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isOsFileDrop(e) || [...(e.dataTransfer?.types || [])].includes(DND_TYPE)) setDropTarget(targetKey);
    },
    onDragLeave: (e) => { e.stopPropagation(); setDropTarget((t) => (t === targetKey ? null : t)); },
    onDrop: (e) => onDropTo(e, targetKey === "root-plans" ? { folderId: null, category: "plans" }
      : targetKey === "root-docs" ? { folderId: null, category: "documents" }
      : { folderId: targetKey, category: foldersById[targetKey]?.category || "plans" }),
  });

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    if (isOsFileDrop(e)) {
      const files = await collectDropped(e.dataTransfer);
      if (files.length) doUpload(files);
    }
  };

  const downloadMarked = async () => {
    if (!drawing || !pageSel.size) return;
    try {
      await downloadMarkedSheets(drawing, [...pageSel], doc, { onProgress: (i, n) => setMarkBusy(`Preparing page ${i + 1} of ${n}…`) });
    } catch {
      toast.error("Marked PDF export failed");
    } finally {
      setMarkBusy("");
    }
  };

  // ---------------- rename ----------------
  const commitRename = async () => {
    if (!renaming) return;
    const name = renaming.name.trim();
    const { kind, id } = renaming;
    setRenaming(null);
    if (!name) { toast.error("Name can't be empty"); return; }
    const current = kind === "file" ? drawingsById[id]?.name : foldersById[id]?.name;
    if (name === current) return;
    try {
      if (kind === "file") await patchDrawing(id, { name });
      else await renameFolder(id, name);
      toast.success(`Renamed to ${name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rename — try a different name");
    }
  };

  const submitNewFolder = async () => {
    const name = folderName.trim();
    if (!name || !newFolder) return;
    try {
      await createFolder(activeId, name, { parentId: newFolder.parentId, category: newFolder.category });
      if (newFolder.parentId) setCollapsed((s) => { const n = new Set(s); n.delete(newFolder.parentId); return n; });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create folder");
      return;
    }
    setNewFolder(null);
    setFolderName("");
  };

  const folderDepth = (f) => {
    let d = 0, cur = f.parent_id || null;
    while (cur && d < 20) { d++; cur = foldersById[cur]?.parent_id || null; }
    return d;
  };

  const renderFileRow = (d, depth = 0) => (
    <PlanFileRow
      key={d.drawing_id} d={d} depth={depth} drawing={drawing}
      renaming={renaming} setRenaming={setRenaming} commitRename={commitRename}
      multiMode={multiMode} sel={sel} toggleSel={toggleSel} startDrag={startDrag}
      fileHasTakeoffs={fileHasTakeoffs} openDrawing={openDrawing}
      moveId={moveId} setMoveId={setMoveId} execMove={execMove}
      folders={folders} folderDepth={folderDepth} setDelFile={setDelFile}
      viewer={viewer}
    />
  );

  const renderFolder = (f, depth) => (
    <PlanFolderNode
      key={f.folder_id} f={f} depth={depth}
      collapsed={collapsed} toggleCollapsed={toggleCollapsed}
      renaming={renaming} setRenaming={setRenaming} commitRename={commitRename}
      multiMode={multiMode} sel={sel} toggleSel={toggleSel} startDrag={startDrag}
      dragOverProps={dragOverProps} dropTarget={dropTarget}
      recursiveFileCount={recursiveFileCount}
      setNewFolder={setNewFolder} setFolderName={setFolderName}
      doUpload={doUpload} setDelFolder={setDelFolder}
      filesIn={filesIn} childFolders={childFolders} renderFileRow={renderFileRow}
      viewer={viewer}
    />
  );

  const section = (category, label, rootKey) => {
    const rootFiles = filesIn(null, category);
    const topFolders = childFolders(null, category);
    const total = rootFiles.length + topFolders.reduce((a, f) => a + recursiveFileCount(f.folder_id), 0);
    return (
      <div className={`${CARD} ${dropTarget === rootKey ? "ring-2 ring-[#1a3fd1]" : ""}`} {...dragOverProps(rootKey)} data-testid={category === "documents" ? "plans-documents" : "plans-section-plans"}>
        <div className="flex items-center gap-1 border-b border-slate-100 px-3 py-2">
          <p className="flex-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">{label} ({total})</p>
          {!viewer && (
          <button aria-label={`New ${label} folder`} title={`New ${label} folder`} className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600"
            onClick={() => { setNewFolder({ parentId: null, category }); setFolderName(""); }}
            data-testid={category === "plans" ? "plans-new-folder" : "docs-new-folder"}><FolderPlus className="h-3 w-3" /></button>
          )}
        </div>
        {category === "plans" && <UploadingRows uploads={uploads} />}
        <div className="space-y-0.5 p-1.5">
          {topFolders.map((f) => renderFolder(f, 0))}
          {rootFiles.map((d) => renderFileRow(d))}
          {!topFolders.length && !rootFiles.length && (
            <p className="px-2 py-2 text-[10px] text-slate-400">{category === "plans" ? "No loose plan files — drop files here or upload a folder." : "Specs, tenders and reports land here automatically."}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`relative h-full space-y-3 overflow-y-auto p-3 ${dragOver ? "bg-orange-50/60" : ""}`}
      onDragOver={(e) => { e.preventDefault(); if (isOsFileDrop(e)) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      data-testid="plans-panel"
    >
      {!viewer && (
      <div className="flex gap-1.5">
        <button onClick={() => folderInput.current?.click()} className="flex-1 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50" data-testid="plans-upload-folder"><FolderUp className="mr-1 inline h-3.5 w-3.5" />Upload folder</button>
        <button onClick={() => filesInput.current?.click()} className="flex-1 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50" data-testid="plans-upload-files"><FileUp className="mr-1 inline h-3.5 w-3.5" />Files / ZIP</button>
        <button
          onClick={() => (multiMode ? exitMulti() : setMultiMode(true))}
          className={`flex-1 rounded-lg border px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider ${multiMode ? "border-[#1a3fd1] bg-[#1a3fd1] text-white" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}
          data-testid="multi-select-toggle">
          <CheckSquare className="mr-1 inline h-3.5 w-3.5" />{multiMode ? "Done" : "Multi Select"}
        </button>
      </div>
      )}
      {!viewer && (
        <button onClick={() => setSendOpen(true)} className="w-full rounded-lg border border-[#1a3fd1]/40 bg-[#1a3fd1]/5 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#1a3fd1] hover:bg-[#1a3fd1]/10" data-testid="plans-send-to-estimate">
          <Send className="mr-1 inline h-3.5 w-3.5" />Send to Estimate
        </button>
      )}
      <SendToEstimateDialog open={sendOpen} onClose={() => setSendOpen(false)} project={activeProject} drawings={drawings} />
      {/* PlanSwift-style upload optimizations — ticked once, remembered for
          every upload until changed */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-0.5" data-testid="upload-optimize-options">
        <label className="flex cursor-pointer items-center gap-1.5 text-[10px] font-semibold text-slate-500" title="Scans above 400 DPI are downsampled to 400 — much smaller, much faster to open. Linework and selectable text stay as-is.">
          <input type="checkbox" className="h-3.5 w-3.5 accent-[#f97316]" checked={!!upOpt.dpi400}
            onChange={(e) => { const n = { ...upOpt, dpi400: e.target.checked }; setUpOpt(n); setUploadOptimize(n); }}
            data-testid="upload-opt-dpi" />
          Reduce to 400 DPI
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 text-[10px] font-semibold text-slate-500" title="Pages are converted to fixed-resolution images — the fastest possible page loads, like PlanSwift. The PDF is replaced; sheet text is no longer searchable (Auto Name still works).">
          <input type="checkbox" className="h-3.5 w-3.5 accent-[#f97316]" checked={!!upOpt.tiff}
            onChange={(e) => { const n = { ...upOpt, tiff: e.target.checked }; setUpOpt(n); setUploadOptimize(n); }}
            data-testid="upload-opt-tiff" />
          Convert to TIFF (fastest)
        </label>
      </div>
      <input ref={filesInput} type="file" multiple accept={ACCEPT_EXT} className="hidden" onChange={(e) => { doUpload([...e.target.files]); e.target.value = ""; }} data-testid="plans-files-input" />
      <input ref={folderInput} type="file" multiple className="hidden" onChange={(e) => { doUpload([...e.target.files]); e.target.value = ""; }} {...{ webkitdirectory: "", directory: "" }} />
      {dragOver && <p className="rounded-lg border-2 border-dashed border-[#f97316] bg-white/70 p-3 text-center text-xs font-semibold text-[#f97316]">Drop files or folders to upload</p>}

      {/* multi-select action bar */}
      {multiMode && (
        <div className="sticky top-0 z-20 flex flex-wrap items-center gap-1.5 rounded-lg border border-[#1a3fd1]/30 bg-white p-1.5 shadow-sm" data-testid="multi-select-bar">
          <span className={`${MONO} px-1 text-[11px] font-semibold text-[#1a3fd1]`} data-testid="multi-select-count">{sel.size} selected</span>
          <button onClick={selectAll} className="rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:bg-slate-100" data-testid="multi-select-all">Select all</button>
          <button onClick={() => setSel(new Set())} className="rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:bg-slate-100" data-testid="multi-select-clear">Clear</button>
          <button onClick={() => sel.size && setMovePicker(true)} disabled={!sel.size} className="rounded bg-[#1a3fd1] px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white disabled:opacity-40" data-testid="multi-select-move">Move to folder</button>
          <button onClick={exitMulti} className="rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:bg-slate-100" data-testid="multi-select-cancel">Cancel</button>
        </div>
      )}
      {movePicker && (() => {
        const payload = selPayload();
        const invalid = new Set();
        payload.folderIds.forEach((fid) => { invalid.add(fid); descendantsOf(fid).forEach((x) => invalid.add(x)); });
        return <MovePicker payload={payload} invalid={invalid} childFolders={childFolders} onPick={(dest) => execMove(payload, dest)} onClose={() => setMovePicker(false)} />;
      })()}

      <div className="flex items-center gap-1.5">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files" className={`${INPUT_SM} flex-1`} data-testid="plans-search" />
        <button onClick={expandAll} className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600" data-testid="plans-expand-all">Expand all</button>
        <button onClick={collapseAll} className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600" data-testid="plans-collapse-all">Collapse all</button>
      </div>

      {newFolder && (
        <div className={`${CARD} flex items-center gap-1.5 p-2`} data-testid="new-folder-form">
          <input autoFocus value={folderName} onChange={(e) => setFolderName(e.target.value)}
            placeholder={newFolder.parentId ? `Subfolder of ${foldersById[newFolder.parentId]?.name}` : `New ${newFolder.category} folder`}
            onKeyDown={(e) => { if (e.key === "Enter") submitNewFolder(); if (e.key === "Escape") setNewFolder(null); }}
            className={`${INPUT_SM} flex-1`} data-testid="plans-folder-name" />
          <button className="rounded-md bg-[#f97316] px-2 py-1 text-[10px] font-semibold uppercase text-white" onClick={submitNewFolder} data-testid="plans-folder-create">Add</button>
          <button aria-label="Cancel new folder" className="rounded-md px-1.5 py-1 text-slate-400 hover:bg-slate-100" onClick={() => setNewFolder(null)}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {section("plans", "Plans", "root-plans")}
      {section("documents", "Documents", "root-docs")}

      {/* bookmarks */}
      {bookmarks.length > 0 && (
        <div className={CARD} data-testid="plans-bookmarks">
          <p className="border-b border-slate-100 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Bookmarks</p>
          <div className="space-y-0.5 p-1.5">
            {bookmarks.map((b) => {
              const key = pageKey(b.drawing.drawing_id, b.page);
              const isRenaming = bmRename?.key === key;
              const commit = () => {
                const v = (bmRename?.value || "").trim();
                mutate((doc2) => { const pg = doc2.pages[key]; if (pg) pg.name = v || `Page ${b.page}`; });
                setBmRename(null);
              };
              return (
              <div key={key} className="flex items-center gap-1.5 rounded-lg px-2 py-1 hover:bg-slate-50" title={`${b.drawing.name} · page ${b.page}`}>
                <Bookmark className="h-3 w-3 shrink-0 fill-[#f97316] text-[#f97316]" />
                {isRenaming ? (
                  <>
                    <input autoFocus value={bmRename.value} onChange={(e) => setBmRename({ key, value: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setBmRename(null); }}
                      maxLength={80} placeholder={`Page ${b.page}`} data-testid={`bookmark-rename-input-${b.drawing.drawing_id}-${b.page}`}
                      className="h-6 min-w-0 flex-1 rounded border border-slate-300 bg-white px-1.5 text-xs text-slate-800 focus:border-[#f97316] focus:outline-none" />
                    <button aria-label="Save name" onClick={commit} data-testid={`bookmark-rename-save-${b.drawing.drawing_id}-${b.page}`} className="rounded p-0.5 text-emerald-600 hover:bg-emerald-50"><Check className="h-3.5 w-3.5" /></button>
                    <button aria-label="Cancel" onClick={() => setBmRename(null)} data-testid={`bookmark-rename-cancel-${b.drawing.drawing_id}-${b.page}`} className="rounded p-0.5 text-slate-400 hover:text-slate-700"><X className="h-3.5 w-3.5" /></button>
                  </>
                ) : (
                  <>
                    <button className="min-w-0 flex-1 truncate text-left text-xs text-slate-700" onClick={() => openDrawing(b.drawing, b.page)} data-testid={`bookmark-open-${b.drawing.drawing_id}-${b.page}`}>
                      {sheetLabel(b.name, b.page, b.drawing.name)}
                    </button>
                    <button aria-label="Rename page" className="rounded p-0.5 text-slate-300 hover:text-[#f97316]"
                      onClick={() => setBmRename({ key, value: b.name === `Page ${b.page}` ? "" : b.name })}
                      data-testid={`bookmark-rename-${b.drawing.drawing_id}-${b.page}`}><Pencil className="h-3 w-3" /></button>
                    <button aria-label="Remove bookmark" className="rounded p-0.5 text-slate-300 hover:text-red-500"
                      onClick={() => mutate((doc2) => { const pg = doc2.pages[key]; if (pg) pg.bookmarked = false; })}
                      data-testid={`bookmark-remove-${b.drawing.drawing_id}-${b.page}`}><Trash2 className="h-3 w-3" /></button>
                  </>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* drawing pages mini-list */}
      {drawing && (isPdf(drawing) || isTiff(drawing)) && numPages > 0 && (
        <div className={CARD} data-testid="plans-page-list">
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Drawing pages</p>
            {pageSel.size > 0 && isPdf(drawing) && (
              <button onClick={downloadMarked} className="inline-flex items-center gap-1 rounded-md bg-[#f97316] px-2 py-1 text-[10px] font-semibold uppercase text-white hover:bg-[#fb923c]" data-testid="pages-marked-download">
                <Download className="h-3 w-3" /> Marked PDF ({pageSel.size})
              </button>
            )}
          </div>
          <div className="grid max-h-64 grid-cols-2 gap-0.5 overflow-y-auto p-1.5">
            {Array.from({ length: numPages }, (_, i) => i + 1).map((pg) => {
              const ps = getPageState(doc, drawing.drawing_id, pg);
              const count = (doc.items || []).filter((i) => i.fileId === drawing.drawing_id && i.page === pg).length;
              const active = pg === pageNum;
              return (
                <div key={pg} className={`flex items-center gap-1 rounded px-1.5 py-1 text-[11px] ${active ? "bg-orange-50 ring-1 ring-[#f97316]" : "hover:bg-slate-50"}`} data-testid={`page-row-${pg}`}>
                  <input type="checkbox" checked={pageSel.has(pg)} aria-label={`Select page ${pg}`}
                    onChange={() => setPageSel((s) => { const n = new Set(s); n.has(pg) ? n.delete(pg) : n.add(pg); return n; })}
                    className="accent-[#f97316]" data-testid={`page-select-${pg}`} />
                  <button className="min-w-0 flex-1 truncate text-left text-slate-700" onClick={() => openDrawing(drawing, pg)}>
                    <span className={MONO}>{pg}</span> {ps.name !== `Page ${pg}` ? <span className="text-slate-500">· {ps.name}</span> : null}
                  </button>
                  {count > 0 && <TakeoffDot testid={`takeoff-dot-page-${pg}`} label={`Has takeoffs (${count})`} />}
                  {ps.feetPerUnit ? <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" title={`Scale: ${ps.scaleLabel}`} /> : <span className="h-1.5 w-1.5 rounded-full bg-slate-200" title="No scale" />}
                  {ps.bookmarked && <Bookmark className="h-2.5 w-2.5 fill-[#f97316] text-[#f97316]" />}
                </div>
              );
            })}
          </div>
          {markBusy && <p className="px-3 pb-2 text-[10px] text-slate-400">{markBusy}</p>}
        </div>
      )}

      <ConfirmDialog open={!!delFolder} onClose={() => setDelFolder(null)}
        title={`Delete folder ${delFolder?.name}?`}
        body="The folder is removed; its files and subfolders move up one level — nothing is deleted."
        confirmLabel="Delete folder"
        onConfirm={() => deleteFolder(delFolder.folder_id)} />
      <ConfirmDialog open={!!delFile} onClose={() => setDelFile(null)}
        title={`Delete ${delFile?.name}?`}
        body="The file is removed from this job. Takeoffs drawn on it are deleted with it from the estimate only if you remove them separately."
        onConfirm={() => removeDrawing(delFile.drawing_id)} />
    </div>
  );
}
